// AdminSectionListRow.jsx

import React, { useState } from 'react';

// Components
import Icon from 'svg/Icon';

// CSS
import './AdminSectionListRow.css';

/**
 * AdminSectionListRow
 *
 * Renders a selectable row in an admin section list column.
 *
 * @param {string} title - Primary row label.
 * @param {string} [metaText] - Optional secondary text shown below the title.
 * @param {string} [iconKey] - Optional leading icon key.
 * @param {string} [iconPath] - Optional SVG path used when iconKey cannot resolve.
 * @param {string|number} [iconSize] - Optional leading icon size (default 22).
 * @param {boolean} isSelected - Whether this row is currently selected.
 * @param {function} onSelect - Called when the row is selected.
 * @param {object} theme - Theme colors used for dynamic styling.
 *
 * @returns {ReactNode} The rendered list row.
 */
const AdminSectionListRow = React.memo(({
  title,
  metaText,
  iconKey,
  iconPath,
  iconSize = 18,
  isSelected,
  onSelect,
  theme,
}) => {

  // ----- STATE ----- //

  const [hovered, setHovered] = useState(false);

  // ----- HANDLERS ----- //

  /**
   * Selects the row from click and keyboard interactions.
   */
  const handleSelect = () => {
    if (!onSelect) return;
    onSelect();
  };

  /**
   * Handles keyboard selection when Enter or Space is pressed.
   *
   * @param {KeyboardEvent} event - Keyboard event from the row element.
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  };

  // ----- RENDER ----- //

  return (
    <div
      className="admin-section-list-row-container"
      style={{
        backgroundColor: hovered
          ? theme.buttonBackgroundColor
          : (isSelected ? theme.buttonBackgroundColor : theme.backgroundMuted),
        color: theme.foreground,
      }}
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {iconKey ? (
        <div className="admin-section-list-row-icon" aria-hidden>
          <Icon
            iconKey={iconKey}
            path={iconPath}
            color={(hovered || isSelected) ? theme.buttonHoverLabelColor : theme.foregroundMuted}
            width={iconSize}
            height={iconSize}
          />
        </div>
      ) : null}

      <div className="admin-section-list-row-content">
        <div className="admin-section-list-row-title"
            style={{
              color: (hovered || isSelected) ? theme.buttonHoverLabelColor : theme.foreground,
            }}>
          {title || 'Untitled'}
        </div>

        {metaText ? (
          <div
            className="admin-section-list-row-meta"
            style={{
              color: (hovered || isSelected) ? theme.buttonHoverLabelColor : theme.foregroundMuted,
            }}
          >
            {metaText}
          </div>
        ) : null}
      </div>
    </div>
  );
});

// Dev Tools
AdminSectionListRow.displayName = 'AdminSectionListRow';

export default AdminSectionListRow;
